import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@tanstack/react-query';
import {Icon} from '@iconify/react';
import {fetchUserPage} from '../../../service/authorization';
import UsersPageTable from './components/UsersPageTable';
import ErrorModalWrapper from '../../../incase-domain/components/modals/errorModal/ErrorModalWrapper';
import ErrorModalIcon from '../../../incase-domain/components/modals/errorModal/ErrorModalIcon';
import ErrorModalContent from '../../../incase-domain/components/modals/errorModal/ErrorModalContent';
import ErrorModalCloseButton from '../../../incase-domain/components/modals/errorModal/ErrorModalCloseButton';
import {useImportUsers} from './useImportUsers';
import {importUsersCSVParser} from './utils/importUsersCSVParser';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import PageHeader from '../../components/common/header/PageHeader';
import {CreateUserModal} from './CreateUserModal';
import {EditUserModal} from './EditUserModal';
import {filtersToUserFilterOptions, useUsersListSearchParams} from './usersListSearchParams';

function UsersPage(): JSX.Element {
    const {t} = useTranslation();

    const inputRef = useRef<HTMLInputElement>(null);

    const [createUserModal, setCreateUserModal] = useState(false);
    const [editUserId, setEditUserId] = useState<string | null>(null);
    const [importUserModal, setImportUserModal] = useState(false);
    const [errorModal, setErrorModal] = useState<boolean>(false);
    const [successModal, setSuccessModal] = useState<boolean>(false);
    const [successMsg, setSuccessMsg] = useState<string>('');
    const [errorMsg, setErrorMsg] = useState<string>('');

    const {listParams, setListParams} = useUsersListSearchParams();

    const importUsersMutation = useImportUsers({
        setImportUserModal,
        setErrorModal,
        setSuccessModal,
        setErrorMsg,
        setSuccessMsg,
    });
    const {
        data: users,
        isLoading: isLoadingUsers,
        isFetching: isFetchingUsers,
    } = useQuery(QUERY_KEYS.user.list(listParams), () =>
        fetchUserPage(
            listParams.page,
            listParams.size,
            filtersToUserFilterOptions(listParams.filters),
            listParams.search,
            listParams.sortBy
        )
    );

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const {files} = e.target;
        if (files && files[0]) {
            const file = files[0];
            const isParsedSuccessfully = await importUsersCSVParser(file, setErrorMsg, setImportUserModal, setErrorModal);

            if (isParsedSuccessfully) {
                importUsersMutation.mutate(file);
            }
        }
    };

    return (
        <>
            <div className="l-container">
                <PageHeader title={t('navigation.users')} />
                <input className="u-d-none" type="file" onChange={handleFileChange} ref={inputRef} accept=".csv" />
                <UsersPageTable
                    isLoading={isLoadingUsers}
                    isFetching={isFetchingUsers}
                    users={users}
                    listParams={listParams}
                    setListParams={setListParams}
                    onImportUser={() => {
                        if (inputRef.current) inputRef.current.click();
                    }}
                    onCreateUser={() => setCreateUserModal(true)}
                    onEditUser={(userId: string) => setEditUserId(userId)}
                />
            </div>

            {importUserModal && (
                <ErrorModalWrapper isOpen={importUserModal}>
                    <Icon icon="line-md:uploading-loop" fontSize="5rem" color="#f14668" />
                    <ErrorModalContent
                        title={t('registerPage.importUsers.importing.title')}
                        description={t('registerPage.importUsers.importing.description')}
                    />
                </ErrorModalWrapper>
            )}

            {successModal && (
                <ErrorModalWrapper isOpen={successModal} setIsOpen={setSuccessModal}>
                    <Icon icon="ooui:success" fontSize="5rem" color="#48c774" />
                    <ErrorModalContent
                        title={t('registerPage.importUsers.success.title')}
                        description={successMsg}
                        className="c-successModalContent"
                        titleClassName="c-successModalContent__title"
                    />
                    <ErrorModalCloseButton onClose={() => setSuccessModal(false)} className="c-successModalCloseButton" />
                </ErrorModalWrapper>
            )}

            {errorModal && (
                <ErrorModalWrapper isOpen={errorModal} setIsOpen={setErrorModal}>
                    <ErrorModalIcon />
                    <ErrorModalContent title={t('registerPage.importUsers.error.title')} description={errorMsg} />
                    <ErrorModalCloseButton onClose={() => setErrorModal(false)} />
                </ErrorModalWrapper>
            )}

            <CreateUserModal isOpen={createUserModal} onCancel={() => setCreateUserModal(false)} />

            <EditUserModal isOpen={!!editUserId} onCancel={() => setEditUserId(null)} userId={editUserId || ''} />
        </>
    );
}

export default UsersPage;
